import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  DestroyRef,
  inject,
  Output,
  EventEmitter,
  AfterContentChecked,
} from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { UserService } from '../../../users/services/user.service'
import { MultiSelectModule } from 'primeng/multiselect'
import { FloatLabelModule } from 'primeng/floatlabel'
import { SelectModule } from 'primeng/select'
import { DatePicker } from 'primeng/datepicker'
import { InputTextModule } from 'primeng/inputtext'
import { TextareaModule } from 'primeng/textarea'
import { Fieldset } from 'primeng/fieldset'
import { RadioButton } from 'primeng/radiobutton'
import { AffairModel } from '../../../../core/models/affair.model'
import { CommonModule } from '@angular/common'
import { ToggleSwitch, ToggleSwitchModule } from 'primeng/toggleswitch'
import { Checkbox } from 'primeng/checkbox'
import { InterventionsService } from '../../../interventions/services/interventions.service'
import { UserModel } from '../../../../core/models/user.model'
import { takeUntil } from 'rxjs/operators'
import { Subject, Subscription, timer } from 'rxjs'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { AuthService } from '../../../../core/services/auth.service'
import { finalize, catchError, debounceTime } from 'rxjs/operators'
import { ButtonModule } from 'primeng/button'
import { Router } from '@angular/router'
import { CardModule } from 'primeng/card'
import { InputSwitchModule } from 'primeng/inputswitch'
import { RadioButtonModule } from 'primeng/radiobutton'
import { DropdownModule } from 'primeng/dropdown'
import { CalendarModule } from 'primeng/calendar'
import { MessagesModule } from 'primeng/messages'
import { MessageModule } from 'primeng/message'
import { ContactModel } from '../../../../core/models/contact.model'
import { AffairContactModel } from '../../../../core/models/affair-contact.model'
import { InputTextarea } from 'primeng/inputtextarea'

@Component({
  selector: 'app-affair-schedule-intervention-create',
  standalone: true,
  imports: [
    CommonModule,
    MultiSelectModule,
    FloatLabelModule,
    SelectModule,
    DatePicker,
    InputTextModule,
    TextareaModule,
    Fieldset,
    RadioButton,
    ReactiveFormsModule,
    ToggleSwitchModule,
    Checkbox,
    ProgressSpinnerModule,
    ButtonModule,
    CardModule,
    MessagesModule,
    MessageModule,
    CalendarModule,
    RadioButtonModule,
    DropdownModule,
    InputTextarea,
  ],
  templateUrl: './affair-schedule-intervention-create.component.html',
  styleUrls: ['./affair-schedule-intervention-create.component.scss'],
})
export class AffairScheduleInterventionCreateComponent
  implements OnInit, OnChanges, AfterContentChecked
{
  @Input() affair: AffairModel
  @Input() selectedTechnicians: UserModel[] = []
  @Input() dateRange: Date[] = []
  @Input() droppedDetails: any
  @Input() scheduleData: any
  @Input() interventionForm: FormGroup // Nouveau input pour recevoir le formulaire du parent

  @Output() formStateChanged = new EventEmitter<{
    valid: boolean
    data: any
    isLoading?: boolean
  }>()
  @Output() formError = new EventEmitter<string>()

  private destroyRef = inject(DestroyRef)
  private destroyed$ = new Subject<void>()
  private formChanges$ = new Subject<void>()
  private authService = inject(AuthService)
  private router = inject(Router)

  public users: any[] = []
  public contacts: ContactModel[] = []
  public affairsContacts: AffairContactModel[] = []
  public worksSupervisors: ContactModel[] = []
  public form: FormGroup
  public formInitialized = false
  public currentUser: any = null
  public submitting = false
  public formSubmitted = false
  public formErrorMessage = ''
  public isLoadingName = false
  public isLoadingDescription = false
  public hasPendingChanges = false

  private _savedContacts: any = null
  private _contactsAreLoading = false
  private _contactsLoadedSubscription: Subscription | null = null
  private _lastGenerationTime: number | null = null
  private _initialGenerationDone = false // Flag pour empêcher les appels multiples à la génération
  private _isGeneratingInitial = false // Flag pour empêcher les appels multiples pendant la génération initiale

  constructor(
    private fb: FormBuilder,
    private usersService: UserService,
    private interventionsService: InterventionsService
  ) {}

  ngOnInit() {
    console.log(this.affair)
    // Register the destroy hook
    this.destroyRef.onDestroy(() => {
      this.destroyed$.next()
      this.destroyed$.complete()
    })

    // Utiliser le formulaire parent s'il est fourni, sinon ne rien faire
    if (this.interventionForm) {
      this.form = this.interventionForm
      this.formInitialized = true

      // S'assurer que le formulaire a les champs nécessaires pour les groupes de produits
      if (!this.interventionForm.get('productsData')) {
        this.interventionForm.addControl('productsData', this.fb.control(null))
      }

      // Observer les changements pour mettre à jour nom et description
      this.observeCheckboxChanges()

      // Charger les contacts
      this.loadContactSelects()

      // Initialisation unique de la génération avec un délai pour garantir que tout est chargé
      // On ne la refait pas de façon automatique
      setTimeout(() => {
        this.ensureAffairProductsInitialized()
        this.triggerNameAndDescriptionGeneration(true) // true = initialisation
      }, 1000)

      // Émettre les données initiales
      setTimeout(() => this.emitFormState(), 0)
    } else {
      // No parent form provided to intervention create component
    }

    // Charger la liste des utilisateurs
    this.usersService.getForSelect().subscribe((users) => {
      this.users = users

      // Si le formulaire est déjà initialisé, mettons à jour les techniciens
      if (this.formInitialized) {
        this.setupSelectedTechnicians()
      }
    })

    // Récupérer l'utilisateur connecté
    this.authService
      .getCurrentUser()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.currentUser = user
      })
  }

  ngOnChanges(changes: SimpleChanges) {
    // Vérifier si le formulaire parent est nouvellement fourni
    if (
      changes['interventionForm'] &&
      changes['interventionForm'].currentValue
    ) {
      const newForm = changes['interventionForm'].currentValue

      if (!this.form || this.form !== newForm) {
        this.form = newForm
        this.formInitialized = true

        // Observer les changements
        this.observeCheckboxChanges()
        // Si nous avons déjà des données de context, mettons à jour le formulaire
        if (this.droppedDetails) {
          this.form.patchValue({
            productData: this.droppedDetails.productData || null,
            erpSource: this.selectedTechnicians[0].erpSource || null,
            affairProducts: [
              {
                affairProductId: this.droppedDetails.productData?.id || null,
                quantity: this.droppedDetails.productData?.quantity || 1,
              },
            ],
          })

          // Charger les contacts
          this.loadContactSelects()

          // Générer le nom et la description
          //this.generateNameAndDescription()
        }

        // Émettre l'état du formulaire
        this.emitFormState()
      }
    }

    // Mise à jour des détails du produit déposé
    if (
      changes['droppedDetails'] &&
      changes['droppedDetails'].currentValue &&
      this.form
    ) {
      // S'assurer que nous avons un erpSource valide provenant du technicien sélectionné
      const erpSource =
        this.selectedTechnicians && this.selectedTechnicians.length > 0
          ? this.selectedTechnicians[0].erpSource
          : 'mylink'

      // Vérifier si nous avons un groupe de produits ou un produit unique
      if (
        this.droppedDetails.productsData &&
        Array.isArray(this.droppedDetails.productsData)
      ) {
        // Nous avons un groupe de produits
        // Créer un tableau d'affairProducts à partir de tous les produits
        // Utiliser idSrc où Src est erpSource pour chaque produit
        const affairProducts = this.droppedDetails.productsData.map(
          (product: any) => {
            const affairProductId =
              product[
                `id${erpSource.charAt(0).toUpperCase() + erpSource.slice(1)}`
              ] || product.id

            return {
              affairProductId: affairProductId,
              quantity: product.quantity || 1,
              erpSource: erpSource, // Toujours utiliser l'erpSource du technicien
            }
          }
        )

        // Mettre à jour le formulaire avec les données de tous les produits
        this.form.patchValue({
          productData: null, // Effacer le produit unique
          productsData: this.droppedDetails.productsData, // Stocker l'ensemble des produits
          erpSource: erpSource, // Utiliser l'erpSource du technicien sélectionné
          affairProducts: affairProducts,
        })

        // Pas de génération automatique ici - uniquement initialisation des produits
        setTimeout(() => {
          this.ensureAffairProductsInitialized()
        }, 100)
      } else {
        // Cas d'un produit unique
        const product = this.droppedDetails.productData
        const affairProductId = product
          ? product[
              `id${erpSource.charAt(0).toUpperCase() + erpSource.slice(1)}`
            ] || product.id
          : null

        this.form.patchValue({
          productData: product || null,
          productsData: null, // Effacer les produits multiples
          erpSource: erpSource, // Utiliser l'erpSource du technicien sélectionné
          affairProducts: [
            {
              affairProductId: affairProductId,
              quantity: product?.quantity || 1,
              erpSource: erpSource, // Toujours utiliser l'erpSource du technicien
            },
          ],
        })

        // Pas de génération automatique ici - uniquement initialisation des produits
        setTimeout(() => {
          this.ensureAffairProductsInitialized()
        }, 100)
      }
    }

    // Mettre à jour les techniciens sélectionnés
    if (
      changes['selectedTechnicians'] &&
      changes['selectedTechnicians'].currentValue &&
      this.formInitialized
    ) {
      this.setupSelectedTechnicians()

      // Mettre à jour l'erpSource dans tous les affairProducts
      setTimeout(() => {
        this.ensureAffairProductsInitialized()
      }, 0)
    }

    // Mettre à jour la plage de dates
    if (
      changes['dateRange'] &&
      changes['dateRange'].currentValue &&
      this.form
    ) {
      this.form.patchValue({
        dateRange: this.dateRange,
      })
    }
  }

  // Mettre à jour le cycle de vie pour émettre les changements d'état du formulaire
  ngAfterContentChecked() {
    if (this.form) {
      this.emitFormState()
    }
  }

  // Émettre l'état du formulaire de manière centralisée
  private emitFormState() {
    this.formStateChanged.emit({
      valid: this.form.valid,
      data: this.prepareFormData(),
      isLoading: this.isLoadingName || this.isLoadingDescription,
    })
  }

  // Nouvelle méthode pour configurer les techniciens sélectionnés
  setupSelectedTechnicians() {
    if (
      !this.form ||
      !this.selectedTechnicians ||
      this.selectedTechnicians.length === 0
    )
      return

    // Transformer les objets techniciens en valeurs IDs pour le multi-select
    const technicianIds = this.selectedTechnicians.map((tech) => tech.id)

    // Mettre à jour le formulaire avec les IDs des techniciens
    this.form.get('techniciansIds')?.setValue(technicianIds)
  }

  // Mise à jour de la méthode pour observer les changements avec debounce
  observeCheckboxChanges() {
    // Liste des contrôles à observer (checkboxes et contacts)
    const checkboxControls = [
      'isDeliveredHouse',
      'isWaitingCheck',
      'recoverCheck',
      'isOffice',
      'doNotShift',
    ]

    const contactControls = ['contacts', 'affairsContacts', 'worksSupervisorId']

    // Observer les checkboxes avec un seul écouteur partagé pour limiter les appels
    checkboxControls.forEach((controlName) => {
      const control = this.form.get(controlName)
      if (control) {
        control.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
          this.formChanges$.next()
        })
      }
    })

    // Observer les champs de contact avec un seul écouteur partagé
    contactControls.forEach((controlName) => {
      const control = this.form.get(controlName)
      if (control) {
        control.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
          this.formChanges$.next()
        })
      }
    })

    // Mise en place du debounce avec délai plus long pour éviter les appels multiples
    this.formChanges$
      .pipe(
        debounceTime(500), // Augmenté à 500ms pour éviter les appels trop fréquents
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        // Ne déclencher la génération que si l'initialisation est terminée
        if (!this._initialGenerationDone) {
          return
        }

        if (this.isLoadingName || this.isLoadingDescription) {
          this.hasPendingChanges = true
        } else {
          // Génération uniquement sur changement de checkbox/select (cas valide)
          this.triggerNameAndDescriptionGeneration(false)
        }
      })
  }

  // Ajouter une nouvelle méthode publique pour forcer la génération du nom et de la description
  // Avec un flag pour indiquer si c'est l'initialisation ou un changement utilisateur
  public triggerNameAndDescriptionGeneration(
    isInitialization: boolean = false
  ): void {
    // Pour l'initialisation, vérifier si c'est déjà fait ou en cours
    if (isInitialization) {
      if (this._initialGenerationDone || this._isGeneratingInitial) {
        return
      }
      this._isGeneratingInitial = true
    }

    // Limiter la génération si trop d'appels sont déjà en cours (pour les changements utilisateur)
    const currentTime = new Date().getTime()
    if (
      !isInitialization &&
      this._lastGenerationTime &&
      currentTime - this._lastGenerationTime < 1000
    ) {
      return
    }

    // Assurer qu'aucune génération n'est déjà en cours
    if (this.isLoadingName || this.isLoadingDescription) {
      // Marquer qu'il y a une demande en attente seulement pour les changements utilisateur
      if (!isInitialization) {
        this.hasPendingChanges = true
      }
      return
    }

    // Enregistrer le moment de cette génération
    this._lastGenerationTime = currentTime

    // Forcer la génération
    this.generateNameAndDescriptionForced(isInitialization)
  }

  // Ajouter une méthode de préparation spécifique pour la dernière étape
  public prepareAndTriggerGeneration(): void {
    if (!this.form) return

    // S'assurer que toutes les données sont bien préparées
    this.ensureAffairProductsInitialized()

    // Déclencher la génération - l'étape finale est un cas spécial
    if (!this.isLoadingName && !this.isLoadingDescription) {
      this.triggerNameAndDescriptionGeneration(true)
    }
  }

  // Méthode pour s'assurer que affairProducts est initialisé correctement
  private ensureAffairProductsInitialized(): void {
    if (!this.form) return

    const formValue = this.form.value
    const erpSource = this.resolveErpSource(
      this.selectedTechnicians && this.selectedTechnicians.length > 0
        ? this.selectedTechnicians[0].erpSource
        : formValue.erpSource || 'mylink'
    )

    let affairProducts = formValue.affairProducts || []

    // Si affairProducts est vide mais que nous avons des produits
    if (
      (!affairProducts || affairProducts.length === 0) &&
      (formValue.productsData ||
        this.droppedDetails?.productsData ||
        formValue.productData ||
        this.droppedDetails?.productData)
    ) {
      // Initialiser à partir des différentes sources de données possibles
      if (formValue.productsData && Array.isArray(formValue.productsData)) {
        affairProducts = formValue.productsData.map((product: any) => {
          const affairProductId =
            product[
              `id${erpSource.charAt(0).toUpperCase() + erpSource.slice(1)}`
            ] || product.id
          return {
            affairProductId: affairProductId,
            quantity: product.quantity || 1,
            erpSource: erpSource,
          }
        })
      } else if (
        this.droppedDetails?.productsData &&
        Array.isArray(this.droppedDetails.productsData)
      ) {
        affairProducts = this.droppedDetails.productsData.map(
          (product: any) => {
            const affairProductId =
              product[
                `id${erpSource.charAt(0).toUpperCase() + erpSource.slice(1)}`
              ] || product.id
            return {
              affairProductId: affairProductId,
              quantity: product.quantity || 1,
              erpSource: erpSource,
            }
          }
        )
      } else if (formValue.productData || this.droppedDetails?.productData) {
        const product =
          formValue.productData || this.droppedDetails?.productData
        const affairProductId =
          product[
            `id${erpSource.charAt(0).toUpperCase() + erpSource.slice(1)}`
          ] || product.id

        affairProducts = [
          {
            affairProductId: affairProductId,
            quantity: product.quantity || 1,
            erpSource: erpSource,
          },
        ]
      }

      // Mettre à jour le formulaire
      this.form.patchValue({ affairProducts }, { emitEvent: false })
    } else {
      // S'assurer que chaque élément de affairProducts a le bon erpSource
      if (affairProducts.length > 0) {
        affairProducts = affairProducts.map((product: any) => ({
          ...product,
          erpSource: erpSource,
        }))
        this.form.patchValue({ affairProducts }, { emitEvent: false })
      }
    }
  }

  // Modifier la méthode existante pour utiliser notre nouvelle implémentation
  // et ne pas générer directement
  generateNameAndDescription() {
    if (!this.form) return

    // Empêcher les appels multiples si une génération est déjà en cours
    if (this.isLoadingName || this.isLoadingDescription) return

    const formValue = this.form.value

    // Vérifier les conditions, mais avec des logs pour le débogage
    if (
      !formValue.productsData &&
      !this.droppedDetails?.productData &&
      !formValue.productData
    ) {
      if (this.droppedDetails) {
      }
      return // Ne pas générer si pas de données de produits
    }

    if (!formValue.mlRef && !this.affair?.mlRef) {
      return
    }

    // Utiliser la méthode forcée pour le reste
    this.triggerNameAndDescriptionGeneration(false)
  }

  // Ajouter une méthode sans les vérifications restrictives
  private generateNameAndDescriptionForced(
    isInitialization: boolean = false
  ): void {
    if (!this.form) {
      return
    }

    const formValue = this.form.value

    // S'assurer que l'erpSource est défini correctement
    const erpSource = this.resolveErpSource(
      this.selectedTechnicians && this.selectedTechnicians.length > 0
        ? this.selectedTechnicians[0].erpSource
        : formValue.erpSource || 'mylink'
    )

    // IMPORTANT: S'assurer que affairProducts est correctement initialisé
    let affairProducts = formValue.affairProducts || []

    // Si affairProducts est vide ou contient des entrées avec affairProductId null
    const needsInitialization =
      !affairProducts ||
      affairProducts.length === 0 ||
      affairProducts.some(
        (p: any) =>
          p.affairProductId === null || p.affairProductId === undefined
      )

    if (needsInitialization) {
      let productsDataSource = null

      // Déterminer quelle source de données utiliser (par ordre de priorité)
      if (formValue.productsData && Array.isArray(formValue.productsData)) {
        productsDataSource = formValue.productsData
      } else if (
        this.droppedDetails?.productsData &&
        Array.isArray(this.droppedDetails.productsData)
      ) {
        productsDataSource = this.droppedDetails.productsData
      } else if (formValue.productData) {
        productsDataSource = [formValue.productData]
      } else if (this.droppedDetails?.productData) {
        productsDataSource = [this.droppedDetails.productData]
      }

      // Si nous avons une source de données, créer les affairProducts
      if (productsDataSource) {
        affairProducts = productsDataSource.map((product: any) => {
          // Trouver l'ID source approprié
          const propertyName = `id${erpSource.charAt(0).toUpperCase() + erpSource.slice(1)}`
          let affairProductId = product[propertyName]

          // Vérification de sécurité: si l'ID spécifique n'existe pas, utiliser l'ID générique
          if (affairProductId === null || affairProductId === undefined) {
            affairProductId = product.id

            // Si l'ID est toujours null, utiliser un ID de secours
            if (affairProductId === null || affairProductId === undefined) {
              affairProductId = -1 // Utiliser un ID négatif comme marqueur de problème
            }
          }

          return {
            affairProductId: affairProductId,
            quantity: product.quantity || 1,
            erpSource: erpSource,
          }
        })

        // Mettre à jour le formulaire avec les nouveaux affairProducts
        this.form.patchValue({ affairProducts }, { emitEvent: false })
      }
    }

    // Préparer les données à envoyer à l'API
    const requestData = {
      mlRef: formValue.mlRef || this.affair?.mlRef,
      erpSource: erpSource,
      affairProducts: affairProducts,
      isDeliveredHouse: formValue.isDeliveredHouse || false,
      isWaitingCheck: formValue.isWaitingCheck || false,
      recoverCheck: formValue.recoverCheck || false,
      isOffice: formValue.isOffice || false,
      doNotShift: formValue.doNotShift || false,
      doNotShiftReason: formValue.doNotShiftReason || '',
      contacts: formValue.contacts || [],
      affairsContacts: formValue.affairsContacts || [],
      worksSupervisorId: formValue.worksSupervisorId || null,
      productsData:
        formValue.productsData || this.droppedDetails?.productsData || null,
      productData:
        formValue.productData || this.droppedDetails?.productData || null,
    }

    // IMPORTANT: Fixer les problèmes de chargement
    // Réinitialiser explicitement les états de chargement
    this.isLoadingName = true
    this.isLoadingDescription = true
    this.hasPendingChanges = false

    const completedRequests = {
      name: false,
      description: false,
    }

    const checkCompletionAndProcessPending = () => {
      if (completedRequests.name && completedRequests.description) {
        // Marquer que la génération initiale est terminée si c'était une initialisation
        if (isInitialization) {
          this._initialGenerationDone = true
          this._isGeneratingInitial = false
        }

        if (this.hasPendingChanges) {
          this.hasPendingChanges = false
          setTimeout(() => this.generateNameAndDescriptionForced(false), 0)
        }
      }
    }

    // Générer le nom
    this.interventionsService
      .getGeneratedName(requestData)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.isLoadingName = false
          completedRequests.name = true
          checkCompletionAndProcessPending()
        })
      )
      .subscribe({
        next: (name) => {
          if (name) {
            this.form.patchValue({ name }, { emitEvent: false })
          }
        },
        error: (err) => {
          this.isLoadingName = false // Assurez-vous de réinitialiser en cas d'erreur aussi
        },
      })

    // Générer la description
    this.interventionsService
      .getGeneratedDescription(requestData)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this.isLoadingDescription = false
          completedRequests.description = true
          checkCompletionAndProcessPending()
        })
      )
      .subscribe({
        next: (description) => {
          if (description) {
            this.form.patchValue(
              { generatedDescription: description },
              { emitEvent: false }
            )
          }
        },
        error: (err) => {
          this.isLoadingDescription = false // Assurez-vous de réinitialiser en cas d'erreur aussi
        },
      })
  }

  // Cette méthode sera appelée par le composant parent
  public submitForm(): Promise<any> {
    if (this.form.invalid) {
      this.markFormGroupTouched(this.form)
      return Promise.reject('Formulaire invalide')
    }

    this.submitting = true
    this.formErrorMessage = ''

    // Préparer les données pour la soumission
    const formData = this.prepareFormData()

    // Retourner une promesse pour permettre au parent de gérer le résultat
    return this.interventionsService
      .store(formData)
      .pipe(
        finalize(() => (this.submitting = false)),
        catchError((err) => {
          const errorMessage =
            err.error?.message ||
            "Une erreur est survenue lors de la création de l'intervention"
          this.formErrorMessage = errorMessage
          this.formError.emit(errorMessage)
          throw err
        })
      )
      .toPromise()
  }

  /**
   * Fix timezone offset issue by preserving the local time values
   * @param date The date to fix
   * @returns A new date with corrected timezone handling
   */
  private fixTimezoneOffset(date: Date): Date {
    const tzOffset = date.getTimezoneOffset() * 60000 // offset in milliseconds
    return new Date(date.getTime() - tzOffset)
  }

  // Ajouter cette méthode pour valider le formulaire (sera appelée par le parent)
  public isFormValid(): boolean {
    return this.form?.valid || false
  }

  // Ajouter cette méthode pour obtenir les données du formulaire
  public getFormData(): any {
    return this.prepareFormData()
  }

  // Préparer les données du formulaire pour la soumission
  private prepareFormData() {
    const formValue = this.form.value
    const scheduleData = this.scheduleData || {}

    // S'assurer que nous avons un erpSource valide provenant du technicien sélectionné
    const erpSource = this.resolveErpSource(
      this.selectedTechnicians && this.selectedTechnicians.length > 0
        ? this.selectedTechnicians[0].erpSource
        : 'mylink'
    )

    // Assurer que les affairProducts ont l'erpSource correct
    let affairProducts = formValue.affairProducts || []

    // S'assurer que chaque élément a l'erpSource correct
    affairProducts = affairProducts.map((product: any) => {
      return {
        ...product,
        erpSource: erpSource, // Toujours utiliser l'erpSource du technicien
      }
    })

    // Créer un nouvel objet avec les données des deux formulaires
    const submissionData = {
      mlRef: formValue.mlRef,
      name: formValue.name,
      description: formValue.description,
      generatedDescription: formValue.generatedDescription,
      erpSource: erpSource, // Utiliser l'erpSource du technicien sélectionné
      conformityId: formValue.conformityId,
      startDate: this.formatDateTime(
        this.fixTimezoneOffset(scheduleData.startDate)
      ),
      techniciansIds: scheduleData.techniciansIds,
      userAssistantId: scheduleData.userAssistantId,
      isConfirmed: scheduleData.isConfirmed,

      // Conditionnellement ajouter les propriétés spécifiques au type d'horaire
      ...(scheduleData.isTimeSlot
        ? {
            isTimeSlot: true,
            startTimeSlot: this.formatTime(scheduleData.startTimeSlot),
            endTimeSlot: this.formatTime(scheduleData.endTimeSlot),
            duration: this.formatTime(scheduleData.duration),
          }
        : {
            isTimeSlot: false,
            duration: this.formatTime(scheduleData.duration),
          }),

      // Options additionnelles
      isDeliveredHouse: formValue.isDeliveredHouse,
      isWaitingCheck: formValue.isWaitingCheck,
      recoverCheck: formValue.recoverCheck,
      isOffice: formValue.isOffice,
      doNotShift: formValue.doNotShift,
      doNotShiftReason: formValue.doNotShiftReason,

      // Contacts
      contacts: formValue.contacts,
      affairsContacts: formValue.affairsContacts,

      // Données du produit si nécessaire
      affairProducts: affairProducts,
      worksSupervisorId: formValue.worksSupervisorId,
    }

    return submissionData
  }

  // Méthode utilitaire pour marquer tous les champs comme touchés
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched()
      if ((control as FormGroup).controls) {
        this.markFormGroupTouched(control as FormGroup)
      }
    })
  }

  // Utilitaires pour le formatage des dates et heures
  private formatDateTime(date: any): string {
    // Vérifier que la date est un objet Date valide
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return ''
    return date.toISOString()
  }

  private formatTime(date: any): string {
    // Vérifier que la date est un objet Date valide
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return ''
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${hours}:${minutes}`
  }

  // Méthode pour restaurer les données du formulaire
  public restoreFormData(data: any): void {
    if (!this.form || !data) {
      return
    }

    try {
      // Exclure les champs qui ne devraient pas être restaurés directement
      const fieldsToExclude = ['generatedDescription', 'name']

      // Créer un nouvel objet sans les champs exclus
      const filteredData = { ...data }
      fieldsToExclude.forEach((field) => {
        if (field in filteredData) {
          delete filteredData[field]
        }
      })
      // Extraire et sauvegarder séparément les données de contact
      const contactFields = ['contacts', 'affairsContacts', 'worksSupervisorId']
      const contactData: { [key: string]: any } = {}

      contactFields.forEach((field) => {
        if (field in filteredData) {
          contactData[field] = filteredData[field]
          delete filteredData[field] // Supprimer pour qu'ils ne soient pas appliqués tout de suite
        }
      })

      // Traitement spécial pour les données complexes comme les tableaux ou les objets
      if (Array.isArray(filteredData.affairProducts)) {
        this.form.get('affairProducts')?.setValue(filteredData.affairProducts)
        delete filteredData.affairProducts
      }

      // Gérer à la fois productData et productsData
      if (filteredData.productData) {
        this.form.get('productData')?.setValue(filteredData.productData)
        delete filteredData.productData
      }

      if (filteredData.productsData) {
        this.form.get('productsData')?.setValue(filteredData.productsData)
        delete filteredData.productsData
      }

      // Appliquer les données filtrées au formulaire SAUF les contacts
      this.form.patchValue(filteredData, { emitEvent: false })

      // Stocker les valeurs de contact pour les appliquer plus tard
      const hasContactData = Object.values(contactData).some((val) => !!val)

      if (hasContactData) {
        this._pendingContactValues = contactData

        // Recharger les contacts - ils seront appliqués dans loadContactSelects
        this.loadContactSelects(true)
      }

      // Régénérer le nom et la description si nécessaire après un court délai
      const shouldRegenerate =
        this.form.get('isDeliveredHouse')?.value ||
        this.form.get('isWaitingCheck')?.value ||
        this.form.get('recoverCheck')?.value ||
        this.form.get('isOffice')?.value ||
        hasContactData

      if (shouldRegenerate && this._initialGenerationDone) {
        // Ne régénérer que si l'initialisation est déjà terminée
        setTimeout(() => {
          this.triggerNameAndDescriptionGeneration(false)
        }, 500)
      } else {
        // Notifier immédiatement du changement d'état du formulaire
        setTimeout(() => {
          this.emitFormState()
        }, 0)
      }
    } catch (error) {}
  }

  // Ajouter une variable pour stocker temporairement les valeurs de contact en attente
  private _pendingContactValues: any = null

  // Complètement refaire la méthode loadContactSelects pour gérer correctement les contacts
  loadContactSelects(isRestoring: boolean = false) {
    const formValue = {
      ...this.form.value,
      erpSource: this.form.value.erpSource || this.affair?.erpSource,
    }

    // S'assurer que nous avons la référence mlRef
    if (!formValue.mlRef) {
      return
    }

    // S'assurer que nous avons erpSource
    if (!formValue.erpSource) {
      return
    }

    // Si nous sommes en train de charger les contacts, ne pas relancer une requête
    if (this._contactsAreLoading) {
      return
    }

    // Sauvegarder les valeurs actuelles de contact avant le chargement
    if (this.form) {
      this._savedContacts = {
        contacts: this.form.get('contacts')?.value,
        affairsContacts: this.form.get('affairsContacts')?.value,
        worksSupervisorId: this.form.get('worksSupervisorId')?.value,
      }
    }

    // Utiliser également les valeurs en attente si elles existent
    if (this._pendingContactValues) {
      this._savedContacts = {
        ...this._savedContacts,
        ...this._pendingContactValues,
      }
      this._pendingContactValues = null // Effacer après avoir consolidé
    }

    this._contactsAreLoading = true

    // Annuler toute souscription précédente si elle existe
    if (this._contactsLoadedSubscription) {
      this._contactsLoadedSubscription.unsubscribe()
    }

    // Charger les contacts
    this._contactsLoadedSubscription = this.interventionsService
      .getSelectsForCreate(formValue)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          this._contactsAreLoading = false
          this._contactsLoadedSubscription = null
        })
      )
      .subscribe({
        next: (data) => {
          if (data) {
            // Mettre à jour les listes de contacts
            this.contacts = data.contacts || []
            this.affairsContacts = data.affairContacts || []

            // Maintenant appliquer les valeurs sauvegardées après un court délai
            // pour s'assurer que les listes sont bien disponibles dans l'UI
            setTimeout(() => {
              if (this._savedContacts && this.form) {
                this.form.patchValue({
                  contacts: this._savedContacts.contacts,
                  affairsContacts: this._savedContacts.affairsContacts,
                  worksSupervisorId: this._savedContacts.worksSupervisorId,
                })

                // Après avoir appliqué les valeurs, déclencher la génération du nom/description si nécessaire
                const hasContactValues =
                  this._savedContacts.contacts ||
                  this._savedContacts.affairsContacts ||
                  this._savedContacts.workSupervisorId

                if (hasContactValues) {
                  this.formChanges$.next()
                }
              } else {
                // Initialiser à null seulement s'il n'y a pas de valeurs à restaurer
                this.form.patchValue(
                  {
                    contacts: null,
                    affairsContacts: null,
                    worksSupervisorId: null,
                  },
                  { emitEvent: false }
                )
              }
            }, 100)
          }
        },
        error: (err) => {
          this._contactsAreLoading = false
        },
      })
  }

  /**
   * Résout l'erpSource en forçant 'nrj' si l'affaire est de type inspection (marketTypeId === 5)
   */
  private resolveErpSource(defaultSource: string): string {
    if (this.affair?.marketType?.id === 5) {
      return 'nrj'
    }
    return defaultSource
  }
}
